import classnames from 'classnames'
import styles from './LifestyleGallery.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import { useState, useEffect, useRef, useMemo } from 'react'
import { classesToElements, formatNumber, lerp } from '@/utils'
import gsap from 'gsap'
import { Draggable } from 'gsap/dist/Draggable'
import useWindowResize from '@/hooks/use-window-resize'
import { TRANSITION_DURATION_MS } from '@/data'
import useStore from '@/store'
import normalizeWheel from 'normalize-wheel'
import useBreakpoint, { getIsMobile } from '@/hooks/use-breakpoint'

gsap.registerPlugin(Draggable)

const MIN_IMAGES = 23

const CLASSES = {
  LIFESTYLE_GALLERY: styles.LifestyleGallery,
  INNER: styles.inner,
  TOP_GALLERY: styles.topGallery,
  GALLERY_LIST: styles.topGallery__list,
  GALLERY_ITEM: styles.topGallery__item,
  GALLERY_LIST_POSITIONING: styles.galleryListPositioning,
  GALLERY_ITEM_POSITIONING: styles.galleryListPositioning__item,
  GALLERY_ITEM_INNER: styles.topGallery__itemInner,
  GALLERY_IMAGE: styles.topGallery__image,
  BOTTOM_CONTENT: styles.bottomContent,
  ACTIVE_IMAGES: styles.activeImages,
  TEXT_CONTAINER: styles.textContainer,
  ACTIVE_NUMBER: styles.activeNumber,
  ACTIVE_NUMBER_TEXT: styles.activeNumber__text,
  LENGTH_AND_TITLE_CONTAINER: styles.lengthAndTitleContainer,
  LENGTH: styles.length,
  LENGTH_TEXT: styles.length__text,
  TITLE: styles.title,
  TITLE_TEXT: styles.title__text,
}

const LifestyleGallery = ({ className, title, images }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const $container = useRef()
  const $refs = useRef({})
  const calculations = useRef({})
  const dragDistanceTarget = useRef(0)
  const dragDistanceCurrent = useRef(0)
  const dragDistanceActiveCurrent = useRef(0)
  const dragDistanceActiveTarget = useRef(0)
  const draggableRef = useRef(null)
  const resizeKey = useWindowResize({
    debounce: 800,
  })
  const paddingResizeKey = useWindowResize({
    debounce: 50,
  })
  const hoveringTopGallery = useRef(false)
  const animations = useRef({})
  const unsubRef = useRef(null)
  const loaderAnimationComplete = useStore(state => state.loaderAnimationComplete)
  const mouseCoords = useRef({ x: 0, y: 0 })
  const hasAnimatedIn = useRef(false)
  const allowSetActiveOnDrag = useRef(true)
  const { isMobile } = useBreakpoint()
  const [padding, setPadding] = useState(0)
  const rafTimeoutRef = useRef(null)

  const calculatedImages = useMemo(() => {
    if (images.length < MIN_IMAGES) {
      const formatted = []
      for (let index = 0; index < MIN_IMAGES; index++) {
        const modulo = index % images.length
        const element = images[modulo]
        formatted.push(element)
      }
      return formatted
    }

    return images
  }, [images])

  const polygonRefs = useRef(
    calculatedImages.map(() => ({ target: { top: 15, bottom: 85 }, current: { top: 15, bottom: 85 } })),
  )

  const totalImages = calculatedImages.length

  const activeIndexFormatted = activeIndex === 0 ? 1 : totalImages - activeIndex + 1

  const getElements = () => {
    if (!$container.current) return
    $refs.current = classesToElements(CLASSES, $container.current)
  }

  const calculate = () => {
    getElements()

    // this doesnt work on safari
    // const totalListWidth = $refs.current.GALLERY_LIST.offsetWidth

    // weird spacing fix... needs a bigger gap if even
    // otherwise just use gap
    // const isEven = $refs.current.GALLERY_ITEM_POSITIONING.length % 2 === 0

    // let totalListWidth = -gap * 1.75
    // if (!isEven) {
    //   totalListWidth = -gap
    // }

    // let totalListWidth = gap
    // let totalListWidth = padding
    let totalListWidth = 0

    $refs.current.GALLERY_ITEM_POSITIONING.forEach((item, i) => {
      totalListWidth += item.getBoundingClientRect().width
      if (i !== $refs.current.GALLERY_ITEM_POSITIONING.length - 1) {
        totalListWidth += padding
      }
    })

    const containerOffset = $refs.current.GALLERY_LIST_POSITIONING.offsetLeft
    const initialItemWidth = $refs.current.GALLERY_ITEM_POSITIONING[0].getBoundingClientRect().width
    const initialStartingPointDistance = totalListWidth - containerOffset - initialItemWidth + padding
    const ridicStartingPoint = initialStartingPointDistance + (totalListWidth + padding) * 1000000

    dragDistanceTarget.current = ridicStartingPoint
    dragDistanceCurrent.current = ridicStartingPoint

    const startingPositions = []
    for (let i = 0; i < $refs.current.GALLERY_ITEM_POSITIONING.length; i++) {
      startingPositions[i] = {
        leftOffset: $refs.current.GALLERY_ITEM_POSITIONING[i].offsetLeft - containerOffset,
        width: $refs.current.GALLERY_ITEM_POSITIONING[i].getBoundingClientRect().width,
      }
    }

    calculations.current = {
      gap: padding,
      totalListWidth,
      startingPositions,
      containerOffset,
    }
  }

  const initializeDraggable = () => {
    if (draggableRef.current?.length) {
      draggableRef.current.forEach(ref => {
        ref.kill()
      })
    }

    getElements()
    calculate()

    draggableRef.current = Draggable.create($refs.current.TOP_GALLERY, {
      type: 'x',
      edgeResistance: 0.2,
      onDrag() {
        let multiplier = 1
        if (isMobile) multiplier = 2
        allowSetActiveOnDrag.current = true
        dragDistanceActiveTarget.current = (this.endX - this.startX) * -1 * multiplier
      },
      onDragEnd() {
        dragDistanceTarget.current = dragDistanceTarget.current + dragDistanceActiveTarget.current
        dragDistanceActiveTarget.current = 0
      },
    })
  }

  const raf = () => {
    dragDistanceCurrent.current = lerp(dragDistanceCurrent.current, dragDistanceTarget.current, 0.1)
    dragDistanceActiveCurrent.current = lerp(dragDistanceActiveCurrent.current, dragDistanceActiveTarget.current, 0.1)

    const $items = $refs.current.GALLERY_ITEM
    if (!$items?.length) return

    let activeSlideDistance = 100000000
    let activeSlideIndex = 0
    $items.forEach(($el, i) => {
      const $innerEl = $el.querySelector(`.${CLASSES.GALLERY_ITEM_INNER}`)
      if (!$el || !$innerEl) return
      const dragOffset = dragDistanceCurrent.current + dragDistanceActiveCurrent.current
      const containerOffset = calculations.current.containerOffset
      const { width: itemWidth, leftOffset } = calculations.current.startingPositions[i]

      $el.style.width = `${itemWidth}px`
      // $el.style.maxWidth = `${itemWidth}px`

      const totalListWidth = calculations.current.totalListWidth + padding
      const x =
        totalListWidth - itemWidth - containerOffset - (leftOffset + ((dragOffset + leftOffset) % totalListWidth))
      const elLeftOffset = $el.getBoundingClientRect().left
      const distanceFromLeftPoint = Math.abs(elLeftOffset - containerOffset)

      if (distanceFromLeftPoint < activeSlideDistance) {
        activeSlideIndex = i
        activeSlideDistance = distanceFromLeftPoint
      }

      const { x: mouseX } = mouseCoords.current
      const fromMouseX = mouseX - (elLeftOffset + itemWidth / 2)
      const percentAway = fromMouseX / (itemWidth * 2)
      const normalized = gsap.utils.clamp(0, 1, Math.abs(percentAway))
      // const normalizedInverse = 1 - normalized
      const polygonPercentTop = 15
      const polygonPercentBottom = 100 - polygonPercentTop
      const top = Math.min(polygonPercentTop, polygonPercentTop - normalized * polygonPercentTop)
      const bottom = polygonPercentBottom + normalized * polygonPercentTop

      polygonRefs.current[i].target = {
        top: hoveringTopGallery.current ? top : 0,
        bottom: hoveringTopGallery.current ? bottom : 100,
      }

      polygonRefs.current[i].current.top = lerp(
        polygonRefs.current[i].current.top,
        polygonRefs.current[i].target.top,
        0.1,
      )

      polygonRefs.current[i].current.bottom = lerp(
        polygonRefs.current[i].current.bottom,
        polygonRefs.current[i].target.bottom,
        0.1,
      )

      const topPercent = `${polygonRefs.current[i].current.top}%`
      const bottomPercent = `${polygonRefs.current[i].current.bottom}%`

      gsap.set($innerEl, {
        '--top-left-y': topPercent,
        '--top-right-y': topPercent,
        '--bottom-left-y': bottomPercent,
        '--bottom-right-y': bottomPercent,
      })

      $el.style.transform = `translate(${x}px,0px)`
    })

    if (allowSetActiveOnDrag.current) {
      setActiveIndex(activeSlideIndex)
    }
  }

  const killRaf = () => {
    gsap.ticker.remove(raf)
    if (rafTimeoutRef.current) {
      clearTimeout(rafTimeoutRef.current)
    }
  }

  useEffect(() => {
    const isMobile = getIsMobile(window.innerWidth)

    killRaf()

    let _padding = window.innerWidth * 0.004
    if (isMobile) {
      _padding = window.innerWidth * 0.015
    }

    setPadding(_padding)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paddingResizeKey])

  const reset = () => {
    getElements()
    calculate()
    initializeDraggable()
  }

  useEffect(() => {
    if (!calculatedImages?.length || !loaderAnimationComplete || padding === 0) {
      return
    }

    killRaf()

    reset()

    const galleryItemEls = $refs.current.GALLERY_ITEM
    galleryItemEls.forEach(el => {
      if (!el) return
      el.style.removeProperty('width')
    })

    rafTimeoutRef.current = setTimeout(() => {
      gsap.ticker.add(raf)
    }, 500)

    // Clean up function
    return () => {
      gsap.ticker.remove(raf)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeKey, calculatedImages, loaderAnimationComplete, padding])

  useEffect(() => {
    const animateIn = () => {
      getElements()

      const { GALLERY_LIST, ACTIVE_NUMBER_TEXT, LENGTH_TEXT, TITLE_TEXT } = $refs.current
      const duration = 1.6
      const ease = 'Power4.easeOut'

      const _isMobile = getIsMobile(window.innerWidth)

      gsap.fromTo(
        GALLERY_LIST,
        {
          opacity: 0,
          y: '100%',
        },
        {
          opacity: 1,
          y: 0,
          duration,
          delay: _isMobile ? 0 : duration * 0.3,
          ease,
          stagger: 0.1,
        },
      )

      gsap.to([LENGTH_TEXT, TITLE_TEXT, ACTIVE_NUMBER_TEXT], {
        y: 0,
        stagger: 0.1,
        delay: duration * 0.3,
        duration,
        ease,
      })
    }
    animations.current = {
      animateIn,
    }
  }, [])

  useEffect(() => {
    if (!loaderAnimationComplete || hasAnimatedIn.current) return

    setTimeout(() => {
      if (hasAnimatedIn.current) return
      if (!animations.current.animateIn) return
      animations.current.animateIn()
      hasAnimatedIn.current = true
    }, TRANSITION_DURATION_MS * 0.2)
  }, [loaderAnimationComplete, resizeKey])

  const itemOnClick = i => {
    allowSetActiveOnDrag.current = false
    setActiveIndex(i)
    // const el = $refs.current.GALLERY_ITEM_INNER[i]
    // if (!el) return
    // const offsetLeft = el.getBoundingClientRect().left - calculations.current.containerOffset
    // dragDistanceTarget.current = dragDistanceTarget.current + offsetLeft
  }

  useEffect(() => {
    if (unsubRef.current) unsubRef.current()

    if (isMobile) return

    unsubRef.current = useStore.subscribe(
      state => [state.cursorPosition],
      value => {
        mouseCoords.current = value[0]
      },
    )

    return () => {
      if (unsubRef.current) unsubRef.current()
    }
  }, [resizeKey, isMobile])

  useEffect(() => {
    const handleOnWheel = event => {
      allowSetActiveOnDrag.current = true
      const normalized = normalizeWheel(event)
      const speed = normalized.pixelY
      dragDistanceTarget.current += speed
    }

    // remove event listeners
    window.removeEventListener('mousewheel', handleOnWheel)
    window.removeEventListener('wheel', handleOnWheel)

    if (isMobile) return

    // add event listeners
    window.addEventListener('mousewheel', handleOnWheel)
    window.addEventListener('wheel', handleOnWheel)

    return () => {
      window.removeEventListener('mousewheel', handleOnWheel)
      window.removeEventListener('wheel', handleOnWheel)
    }
  }, [isMobile])

  if (!title || !calculatedImages?.length) return null

  return (
    <div
      className={classnames(CLASSES.LIFESTYLE_GALLERY, className)}
      style={{
        '--padding': `${padding}px`,
      }}
      ref={$container}
    >
      <div className={CLASSES.INNER}>
        <div className={CLASSES.TOP_GALLERY}>
          <ul className={CLASSES.GALLERY_LIST_POSITIONING}>
            {calculatedImages.map((image, i) => (
              <li
                key={i}
                className={CLASSES.GALLERY_ITEM_POSITIONING}
                style={{
                  '--aspect-ratio': image.asset.metadata.dimensions.aspectRatio,
                  // '--aspect-ratio': 1,
                }}
              />
            ))}
          </ul>
          <ul
            className={CLASSES.GALLERY_LIST}
            onMouseLeave={() => {
              if (isMobile) return
              hoveringTopGallery.current = false
            }}
            onMouseEnter={() => {
              if (isMobile) return
              hoveringTopGallery.current = true
            }}
          >
            {calculatedImages.map((image, i) => (
              <li
                key={i}
                className={CLASSES.GALLERY_ITEM}
                style={{
                  '--aspect-ratio': image.asset.metadata.dimensions.aspectRatio,
                  // '--aspect-ratio': 1,
                }}
                onClick={() => {
                  itemOnClick(i)
                }}
              >
                <button
                  className={CLASSES.GALLERY_ITEM_INNER}
                  ariaLabel={`View ${title} Lifestyle image ${formatNumber(i + 1)} of ${totalImages}`}
                >
                  <SanityImage
                    image={image}
                    className={CLASSES.GALLERY_IMAGE}
                    breakpoints={{
                      xs: {
                        width: 300,
                      },
                      tablet: {
                        width: 300,
                      },
                    }}
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className={CLASSES.BOTTOM_CONTENT}>
          <div className={CLASSES.ACTIVE_IMAGES}>
            {calculatedImages.map((image, i) => (
              <SanityImage
                key={i}
                image={image}
                className={classnames(styles.activeImage, {
                  [styles.isActive]: i === activeIndex,
                })}
                priority
                disableLoadedOpacity
                breakpoints={{
                  tablet: {
                    width: 800,
                    options: {
                      height: 800,
                      fit: 'crop',
                    },
                  },
                  xs: {
                    width: 600,
                    options: {
                      height: 600,
                      fit: 'crop',
                    },
                  },
                }}
              />
            ))}
          </div>
          <div className={CLASSES.TEXT_CONTAINER}>
            <p className={CLASSES.ACTIVE_NUMBER}>
              <span className={CLASSES.ACTIVE_NUMBER_TEXT}>{formatNumber(activeIndexFormatted, 3)}</span>
            </p>
            <div className={CLASSES.LENGTH_AND_TITLE_CONTAINER}>
              <p className={CLASSES.LENGTH}>
                <span className={CLASSES.LENGTH_TEXT}>
                  {formatNumber(activeIndexFormatted, 3)} - {formatNumber(totalImages, 3)}
                </span>
              </p>
              <h2 className={CLASSES.TITLE}>
                <span className={CLASSES.TITLE_TEXT}>{title}</span>
              </h2>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

LifestyleGallery.displayName = 'LifestyleGallery'

export default LifestyleGallery
